import { z } from 'zod';

import {
  PAYMENT_PLAN_FIELD_LABELS,
  paymentPlanFormSchema,
} from '#/modules/srmMgmt/purchaseOrder/schemas/paymentPlanFormSchema';
import { PURCHASE_ORDER_TAX_TYPE_OPTIONS } from '#/modules/srmMgmt/purchaseOrder/schemas/purchaseOrderSchema';

const amendmentLineSchema = z.object({
  mtrId: z.string().min(1, '請選擇物料'),
  mtrNo: z.string().optional().nullable(),
  mtrName: z.string().optional().nullable(),
  unitId: z.string().optional().nullable(),
  orderQty: z.union([z.string(), z.number(), z.null()]),
  cancelQty: z.union([z.string(), z.number(), z.null()]).optional().nullable(),
  unitPrice: z.union([z.string(), z.number(), z.null()]).optional().nullable(),
  remark: z.string().optional().nullable(),
}).superRefine((line, ctx) => {
  const qty = line.orderQty;
  const orderN = Number(qty);
  if (qty === '' || qty == null || Number.isNaN(orderN) || orderN < 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '訂購數量須 ≥ 1',
      path: ['orderQty'],
    });
  }

  const cancelRaw = line.cancelQty;
  const cancelN = cancelRaw === '' || cancelRaw == null ? 0 : Number(cancelRaw);
  if (Number.isNaN(cancelN) || cancelN < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '取消數量不可為負',
      path: ['cancelQty'],
    });
  } else if (!Number.isNaN(orderN) && cancelN > orderN) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '取消數量不可大於訂購數量',
      path: ['cancelQty'],
    });
  }

  if (line.unitPrice !== '' && line.unitPrice != null) {
    const price = Number(line.unitPrice);
    if (Number.isNaN(price) || price < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '單價不可為負',
        path: ['unitPrice'],
      });
    }
  }
});

export const purchaseOrderAmendmentFormSchema = z.object({
  taxType: z.enum(['TAX_EXCLUDED', 'TAX_INCLUDED', 'TAX_EXEMPT'], {
    message: '請選擇稅別',
  }),
  taxRate: z.union([z.string(), z.number()]).refine((value) => {
    const n = Number(value);
    return Number.isFinite(n) && n >= 0;
  }, { message: '稅率不可為負' }),
  roundingMode: z.enum(['UP', 'DOWN', 'HALF_UP']).optional().nullable(),
  issueReason: z.string().min(1, '請填寫異動事由'),
  remark: z.string().optional().nullable(),
  lines: z.array(amendmentLineSchema).min(1, '至少一筆明細').superRefine((lines, ctx) => {
    const seen = new Set();
    lines.forEach((line, index) => {
      const id = line.mtrId?.trim();
      if (!id) {
        return;
      }
      if (seen.has(id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '同一物料不可重複',
          path: [index, 'mtrId'],
        });
        return;
      }
      seen.add(id);
    });
  }),
  paymentPlan: paymentPlanFormSchema,
});

export const PURCHASE_ORDER_AMENDMENT_FIELD_LABELS = {
  taxType: '稅別',
  taxRate: '稅率',
  issueReason: '異動事由',
  remark: '備註',
  lines: '明細',
  'lines.mtrId': '物料',
  'lines.unitId': '單位',
  'lines.orderQty': '訂購數量',
  'lines.cancelQty': '取消數量',
  'lines.unitPrice': '單價',
  'lines.remark': '行備註',
  ...PAYMENT_PLAN_FIELD_LABELS,
};

export const PURCHASE_ORDER_AMENDMENT_LINES_TIP =
  '可調整訂購／取消數量與單價；有效量不可低於已入庫／在途進貨。不改供應商、幣別、匯率與到貨日。';

export { PURCHASE_ORDER_TAX_TYPE_OPTIONS };
